import TextEditor from 'components/Editor';
import {
  changeTodos,
  EditorTodoType,
  filterTodosContent,
} from 'components/Editor/utils';
import React, { FC, useCallback, useEffect } from 'react';
import { graphql, useFragment } from 'utils/graphClient';
import { PageEditor_node$key } from './__generated__/PageEditor_node.graphql';
import { CloseConfirmationDialog } from './components/CloseConfirmationDialog';
import { ModalBodyHeight } from '../ItemViewerModal';
import { useOpenItemViewer } from '../useOpenItemViewer';

const pageNodeFragment = graphql`
  fragment PageEditor_node on Node {
    ...FolderNodePublishBar_folderNode
    ... on FolderItem {
      id
      name
      publishState
      canRename {
        value
      }
      canUpdate {
        value
      }
      content {
        __typename
        ... on Page {
          id
          title
          data
          todos(first: 500) {
            edges {
              node {
                id
                __typename
                ...TodoDueDate_todo
                ...TodoAssignee_todo
                text
                documentNode {
                  id
                  ...TodoCard_page
                }
                assignee {
                  id
                }
                state
                dueDate
                publishState
                isListed
              }
            }
          }
        }
      }
      space {
        id
      }
      ...PublishReminderDialog_folderItem
    }
  }
`;

interface PageEditorProps {
  node: PageEditor_node$key;
  onChange: (content: string) => void;
  isUpdatingPage: boolean;
  changed: boolean;
}

export const PageEditor: FC<PageEditorProps> = ({
  node: nodeRef,
  onChange,
  isUpdatingPage,
  changed,
}) => {
  const node = useFragment(pageNodeFragment, nodeRef);
  const { isEditModeEnabled, isOpen, startEditMode } = useOpenItemViewer();
  if (!node.content || node.content.__typename !== 'Page') {
    throw 'this should not happen';
  }

  const { id } = node.content;

  const todos = node?.content.todos?.edges.map((todo) => {
    if (!todo.node?.id || !todo.node.text) return null;

    const editorTodo: EditorTodoType = {
      id: todo.node.id,
      text: todo.node.text,
    };
    return editorTodo;
  });

  const data = changeTodos(node.content.data, todos || []);

  const isDraft = node.publishState === 'DRAFT';
  const isEditable = !!node.canUpdate?.value && isEditModeEnabled;

  // Start viewer in edit mode when draft
  useEffect(() => {
    if (isOpen && isDraft) {
      startEditMode();
    }
  }, [isOpen]);

  const handleChange = useCallback(
    (content: string) => {
      if (isEditable) {
        const filteredContent = filterTodosContent(content);
        onChange(filteredContent);
      }
    },
    [onchange, isEditable],
  );

  return (
    <>
      <TextEditor
        data={data as any}
        pageId={id}
        onChange={handleChange}
        editable={isEditable}
        autoFocus={isEditable}
        spaceId={node.space?.id}
        minHeight={ModalBodyHeight}
        showTemplates
      />

      <CloseConfirmationDialog
        isUpdatingPage={isUpdatingPage}
        changedPage={changed}
        node={node}
        isDraft={node.publishState === 'DRAFT'}
      />
    </>
  );
};
